import type { GradeClozeInput } from '../../server/endpoints/grading'

export type ClozeBlank = {
  answer: string
  answerRegexp?: string
}

export type ClozeGradeInput = {
  blank: ClozeBlank
  clozeMarkdown: string
  userAnswer: string
}

export type ClozeModelGradeResult = {
  correct: boolean
  reason: string
}

export type ClozeGradeMethod = 'exact' | 'regexp' | 'model' | 'failed'

export type ClozeGradeResult = {
  correct: boolean
  method: ClozeGradeMethod
  reason: string
}

export async function gradeClozeAnswer(
  input: ClozeGradeInput,
  gradeCloze?: (input: GradeClozeInput) => Promise<ClozeModelGradeResult>,
): Promise<ClozeGradeResult> {
  const expected = normalizeAnswerText(input.blank.answer)
  const actual = normalizeAnswerText(input.userAnswer)

  if (actual === expected) {
    return {
      correct: true,
      method: 'exact',
      reason: 'normalized exact match',
    }
  }

  if (input.blank.answerRegexp) {
    const regexpMatch = matchesAnswerRegexp(
      input.userAnswer,
      input.blank.answerRegexp,
    )
    // An uncompilable regexp never matches; grading falls through.
    if (regexpMatch === true) {
      return {
        correct: true,
        method: 'regexp',
        reason: 'strict regexp match',
      }
    }
  }

  if (!gradeCloze) {
    return {
      correct: false,
      method: 'failed',
      reason: 'no exact or regexp match and no model fallback',
    }
  }

  const modelGrade = await gradeCloze({
    clozeMarkdown: input.clozeMarkdown,
    expected: input.blank.answer,
    userAnswer: input.userAnswer,
  })
  return {
    correct: modelGrade.correct,
    method: 'model',
    reason: modelGrade.reason,
  }
}

const NEGATIVE = '\uE001'

/**
 * NFC-normalize, lowercase, and replace any run of characters that are not
 * letters, numbers or combining marks (punctuation, whitespace) with a single
 * space. Combining marks are never dropped (they distinguish words in Indic
 * scripts) and a minus directly before a digit, except after a letter/number, is
 * significant ("(-5)" != "(5)"). A leading plus is ignored ("+5" == "5").
 * An answer that normalizes to nothing (e.g. "+" vs "-") compares by its raw
 * trimmed text instead of as equal-empty. Shared with short-answer grading so
 * cloze and SA normalize identically.
 */
export function normalizeAnswerText(text: string): string {
  const nfc = text.normalize('NFC')
  const normalized = nfc
    .toLowerCase()
    .replaceAll(/(?<![\p{L}\p{N}])[-−](?=\p{N})/gu, NEGATIVE)
    .replaceAll(/[^\p{L}\p{N}\p{M}\uE001]+/gu, ' ')
    .trim()
  if (normalized) return normalized
  return nfc.trim().replaceAll(/\s+/g, ' ')
}

export function matchesAnswerRegexp(
  userAnswer: string,
  pattern: string,
): boolean | 'invalid-regexp' {
  try {
    const regexp = new RegExp(pattern, 'i')
    return regexp.test(userAnswer)
  } catch {
    return 'invalid-regexp'
  }
}
